import { useEffect, useState, type CSSProperties, type FormEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  BANKOM_PRESET,
  GMAIL_PRESET,
  MAIL_DEFAULTS,
  mailApi,
  type MailConnection,
  type MailConnectionStatus,
} from "../api/mail";
import type { ApiError } from "../api/client";
import { useToast } from "../components/Toast";
import { useConfirm } from "../components/ConfirmDialog";
import { useAuth } from "../auth/AuthContext";
import { useSectionAccess } from "../auth/useSectionAccess";
import Ic from "../components/icons.jsx";

/**
 * Settings → "Moj mejl". Each internal user connects
 * THEIR OWN mailbox here: the API has no id path and no ?userId=, so this page
 * can only ever show and edit the caller's row.
 *
 * The app-password is write-only: it is never returned by the API, so the
 * field renders empty with a "stored" hint whenever `hasSecret` is true, and
 * an empty field on save means "keep the stored one".
 *
 * Owning the row is not the whole gate. Each request also carries the level of
 * the `personal_mail` section, and `mail:connect` on top of it:
 *
 *   GET    /api/mail/connection       mail:connect + section:personal_mail:read
 *   PUT    /api/mail/connection       mail:connect + section:personal_mail:write
 *   POST   /api/mail/connection/test  mail:connect + section:personal_mail:write
 *   DELETE /api/mail/connection       mail:connect + section:personal_mail:full
 *
 * The route and the menu entry guard only `:read`, and `mail:connect` is the
 * section's WRITE action — so a user whose level was lowered to read opens this
 * screen and every single request from it answers 403, their own mailbox or
 * not. Each control therefore stands behind the level its own request needs,
 * and where one is gone a sentence says why.
 */
export default function MailSettingsPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "en";
  const toast = useToast();
  const confirm = useConfirm();
  const { hasPermission } = useAuth();
  const { canRead, canWrite, canFull } = useSectionAccess("personal_mail");
  const smeDaVidi = canRead && hasPermission("mail:connect");
  const smeDaCuva = smeDaVidi && canWrite;
  const smeDaIskljuci = smeDaCuva && canFull;

  const [conn, setConn] = useState<MailConnection | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState(String(MAIL_DEFAULTS.smtpPort));
  const [smtpStarttls, setSmtpStarttls] = useState<boolean>(MAIL_DEFAULTS.smtpStarttls);
  const [imapHost, setImapHost] = useState("");
  const [imapPort, setImapPort] = useState(String(MAIL_DEFAULTS.imapPort));
  const [imapSsl, setImapSsl] = useState<boolean>(MAIL_DEFAULTS.imapSsl);
  const [username, setUsername] = useState("");
  const [secret, setSecret] = useState("");

  function applyConnection(c: MailConnection | null) {
    setConn(c);
    if (!c) return;
    setEmail(c.email);
    setDisplayName(c.displayName ?? "");
    setSmtpHost(c.smtpHost);
    setSmtpPort(String(c.smtpPort));
    setSmtpStarttls(c.smtpStarttls);
    setImapHost(c.imapHost);
    setImapPort(String(c.imapPort));
    setImapSsl(c.imapSsl);
    setUsername(c.username);
    setSecret(""); // never rendered back — hasSecret is all the API says
  }

  useEffect(() => {
    // Reading the connection already needs mail:connect, so without it there is
    // nothing to fetch — and a request that can only answer 403 would reach the
    // reader as a raw server error instead of the sentence below.
    if (!smeDaVidi) {
      setLoading(false);
      return;
    }
    let live = true;
    void mailApi
      .getConnection()
      .then((c) => {
        if (live) applyConnection(c);
      })
      .catch((err) => {
        if (live) toast.error((err as ApiError).message);
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [toast, smeDaVidi]);

  function applyPreset(preset: typeof BANKOM_PRESET | typeof GMAIL_PRESET) {
    setSmtpHost(preset.smtpHost);
    setSmtpPort(String(preset.smtpPort));
    setSmtpStarttls(preset.smtpStarttls);
    setImapHost(preset.imapHost);
    setImapPort(String(preset.imapPort));
    setImapSsl(preset.imapSsl);
    if (!username.trim() && email.trim()) setUsername(email.trim());
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const saved = await mailApi.saveConnection({
        email: email.trim(),
        displayName: displayName.trim() || null,
        smtpHost: smtpHost.trim(),
        smtpPort: smtpPort === "" ? null : Number(smtpPort),
        smtpStarttls,
        imapHost: imapHost.trim(),
        imapPort: imapPort === "" ? null : Number(imapPort),
        imapSsl,
        username: username.trim(),
        // Empty means "keep the stored app-password" — the backend only
        // requires it when creating the connection.
        secret: secret ? secret : null,
      });
      applyConnection(saved);
      toast.success(t("mail.settings.saved", "Mailbox saved. Run the connection test now."));
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setSaving(false);
    }
  }

  async function test() {
    if (testing) return;
    setTesting(true);
    try {
      const tested = await mailApi.testConnection();
      applyConnection(tested);
      if (tested.status === "OK") toast.success(t("mail.settings.testOk", "Connection works."));
      else toast.error(tested.lastError ?? t("mail.settings.testFailed", "Connection failed."));
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setTesting(false);
    }
  }

  async function disconnect() {
    const ok = await confirm({
      title: t("mail.settings.disconnectTitle", "Disconnect mailbox?"),
      message: t(
        "mail.settings.disconnectBody",
        "The portal stops sending from this address and stops capturing replies. Already archived messages stay.",
      ),
      danger: true,
    });
    if (!ok) return;
    try {
      await mailApi.deleteConnection();
      setConn(null);
      setSecret("");
      toast.success(t("mail.settings.disconnected", "Mailbox disconnected."));
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  if (loading) {
    return <p style={{ color: "var(--ink-3)" }}>{t("common.loading", "Loading…")}</p>;
  }

  // Testing the connection is a POST, so it asks for the same write level as saving.
  const canTest = conn != null && !testing && smeDaCuva;

  return (
    <div className="fadein" style={{ display: "flex", flexDirection: "column", gap: 18, maxWidth: 860 }}>
      <div className="page-head">
        <div>
          <h1>{t("mail.settings.title", "My email")}</h1>
          <div className="sub">
            {t("mail.settings.subtitle", "Send offers from your own address and capture replies on the lead timeline.")}
          </div>
        </div>
        <div className="page-actions">
          {smeDaCuva && (
            <button type="button" className="btn" onClick={() => void test()} disabled={!canTest}>
              <Ic.refresh />{" "}
              {testing ? t("mail.settings.testing", "Testing…") : t("mail.settings.test", "Test connection")}
            </button>
          )}
          {conn &&
            (smeDaIskljuci ? (
              <button type="button" className="btn ghost" onClick={() => void disconnect()}>
                <Ic.x /> {t("mail.settings.disconnect", "Disconnect")}
              </button>
            ) : (
              <span style={muted}>
                {t("mail.settings.noDisconnectRight", "Isključivanje sandučeta traži puno pravo na „Moj mejl“.")}
              </span>
            ))}
        </div>
      </div>

      <section className="card">
        <header className="card-h">
          <h3>{t("mail.settings.statusTitle", "Status")}</h3>
          <StatusBadge status={conn?.status ?? null} />
        </header>
        <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {!smeDaCuva && (
            <p style={muted}>
              {t(
                "mail.settings.noConnectRight",
                "Vaš nalog ima samo pravo čitanja na „Moj mejl“, pa sanduče odavde ne može da se poveže ni izmeni. Pravo dodeljuje administrator.",
              )}
            </p>
          )}
          {!conn && smeDaCuva && (
            <p style={muted}>{t("mail.settings.statusNone", "No mailbox connected yet. Fill in the form below and save.")}</p>
          )}
          {conn?.status === "OK" && (
            <p style={muted}>
              {t("mail.settings.statusOk", "SMTP and IMAP both answered. Sending and reply capture are active.")}
            </p>
          )}
          {conn?.status === "UNVERIFIED" && (
            <p style={muted}>
              {t("mail.settings.statusUnverified", "Saved but not verified. Sending stays blocked until the test passes.")}
            </p>
          )}
          {conn?.status === "ERROR" && conn.lastError && <p style={errText}>{conn.lastError}</p>}
          {conn?.lastCheckedAt && (
            <p style={muted}>
              {t("mail.settings.lastChecked", "Last checked")}: {new Date(conn.lastCheckedAt).toLocaleString(locale)}
            </p>
          )}
        </div>
      </section>

      {/* The form and its Save button are the write level; a reader without it gets the
          sentence in the status card above instead of a form that cannot be saved. */}
      {smeDaCuva && (
        <section className="card">
          <header className="card-h">
            <h3>{t("mail.settings.formTitle", "Mailbox")}</h3>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" className="btn sm" onClick={() => applyPreset(BANKOM_PRESET)}>
                {t("mail.settings.bankomPreset", "Bankom")}
              </button>
              <button type="button" className="btn sm" onClick={() => applyPreset(GMAIL_PRESET)}>
                {t("mail.settings.gmailPreset", "Gmail")}
              </button>
            </div>
          </header>
          <div className="card-b">
            <div style={noteBox}>
              <strong>{t("mail.settings.appPasswordTitle", "Your mailbox password stays encrypted.")}</strong>
              <div style={{ marginTop: 4 }}>
                {t(
                  "mail.settings.appPasswordBody",
                  "Enter the password of your own mailbox (mail.bankom.rs for Bankom addresses). Some providers — Gmail among them — do not accept the account password here and require a generated app-password with 2-step verification enabled.",
                )}
              </div>
            </div>

            <form onSubmit={save} style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 14 }}>
              <div style={grid}>
                <Field label={t("mail.settings.email", "Email address")} required>
                  <input
                    className="input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="ime.prezime@bankom.rs"
                    required
                  />
                </Field>
                <Field label={t("mail.settings.displayName", "Display name")}>
                  <input
                    className="input"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder={t("mail.settings.displayNamePlaceholder", "Name shown to recipients")}
                  />
                </Field>
              </div>

              <div style={grid}>
                <Field label={t("mail.settings.smtpHost", "SMTP host")} required>
                  <input className="input" value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} required />
                </Field>
                <Field label={t("mail.settings.smtpPort", "SMTP port")}>
                  <input
                    className="input"
                    type="number"
                    min={1}
                    max={65535}
                    value={smtpPort}
                    onChange={(e) => setSmtpPort(e.target.value)}
                  />
                </Field>
              </div>
              <label style={checkRow}>
                <input type="checkbox" checked={smtpStarttls} onChange={(e) => setSmtpStarttls(e.target.checked)} />
                <span>{t("mail.settings.smtpStarttls", "SMTP uses STARTTLS")}</span>
              </label>

              <div style={grid}>
                <Field label={t("mail.settings.imapHost", "IMAP host")} required>
                  <input className="input" value={imapHost} onChange={(e) => setImapHost(e.target.value)} required />
                </Field>
                <Field label={t("mail.settings.imapPort", "IMAP port")}>
                  <input
                    className="input"
                    type="number"
                    min={1}
                    max={65535}
                    value={imapPort}
                    onChange={(e) => setImapPort(e.target.value)}
                  />
                </Field>
              </div>
              <label style={checkRow}>
                <input type="checkbox" checked={imapSsl} onChange={(e) => setImapSsl(e.target.checked)} />
                <span>{t("mail.settings.imapSsl", "IMAP uses SSL/TLS")}</span>
              </label>

              <div style={grid}>
                <Field label={t("mail.settings.username", "Username")} required>
                  <input
                    className="input"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder={t("mail.settings.usernamePlaceholder", "Usually the full email address")}
                    required
                  />
                </Field>
                <Field
                  label={t("mail.settings.secret", "Mailbox password")}
                  required={!conn?.hasSecret}
                  hint={
                    conn?.hasSecret
                      ? t("mail.settings.secretStored", "Stored — leave empty to keep it.")
                      : t("mail.settings.secretRequired", "Required to connect the mailbox.")
                  }
                >
                  <input
                    className="input"
                    type="password"
                    value={secret}
                    onChange={(e) => setSecret(e.target.value)}
                    autoComplete="new-password"
                    placeholder={conn?.hasSecret ? "••••••••••••••••" : ""}
                    required={!conn?.hasSecret}
                  />
                </Field>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button type="submit" className="btn primary" disabled={saving}>
                  <Ic.check /> {saving ? t("common.saving", "Saving…") : t("action.save", "Save")}
                </button>
              </div>
            </form>
          </div>
        </section>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: MailConnectionStatus | null }) {
  const { t } = useTranslation();
  if (status === "OK") {
    return <span style={badge("var(--ok)")}>{t("mail.status.OK", "Working")}</span>;
  }
  if (status === "ERROR") {
    return <span style={badge("var(--err)")}>{t("mail.status.ERROR", "Error")}</span>;
  }
  if (status === "UNVERIFIED") {
    return <span style={badge("var(--warn)")}>{t("mail.status.UNVERIFIED", "Not verified")}</span>;
  }
  return <span style={badge("var(--ink-4)")}>{t("mail.status.NONE", "Not connected")}</span>;
}

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
      <span style={{ color: "var(--ink-3)" }}>
        {label} {required && <span style={{ color: "var(--err)" }}>*</span>}
      </span>
      {children}
      {hint && <span style={{ fontSize: 12, color: "var(--ink-3)" }}>{hint}</span>}
    </label>
  );
}

function badge(color: string): CSSProperties {
  return {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 10px",
    borderRadius: 999,
    border: `1px solid ${color}`,
    color,
    fontSize: 11.5,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    whiteSpace: "nowrap",
  };
}

const grid: CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 14 };
const checkRow: CSSProperties = { display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--ink-2)", cursor: "pointer" };
const muted: CSSProperties = { margin: 0, fontSize: 13, color: "var(--ink-3)" };
const errText: CSSProperties = { margin: 0, fontSize: 13, color: "var(--err)", wordBreak: "break-word" };
const noteBox: CSSProperties = {
  padding: 12,
  borderRadius: 8,
  background: "var(--warn-bg)",
  border: "1px solid var(--warn)",
  color: "var(--ink)",
  fontSize: 13,
  lineHeight: 1.5,
};
